import { useCallback, useMemo, useSyncExternalStore } from 'react'

import { type UrlParam, useUrlParams } from '@/lib/url/url-param.hook'

/**
 * Singleton store that holds an ordered list of row IDs from a table/grid.
 * Used by drawer components to navigate between items (prev/next) via
 * `useSyncExternalStore`, keeping navigation in sync with the visible table order.
 */
class DrawerNavigationStore {
  private ids: string[] = []
  private listeners = new Set<() => void>()

  private notify = () => {
    this.listeners.forEach((listener) => listener())
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  getSnapshot = (): string[] => this.ids

  setIds = (nextIds: string[]): void => {
    if (nextIds.length === this.ids.length && nextIds.every((id, i) => id === this.ids[i])) return
    this.ids = nextIds
    this.notify()
  }
}

const store = new DrawerNavigationStore()

// ── Hooks ────────────────────────────────────────────────────────────────────

/**
 * Called by table components to push visible row IDs into the store.
 * Returns a stable callback suitable for ag-grid's onModelUpdated.
 * Does not clear on unmount — setIds fully replaces the data when a new
 * tab/grid mounts, avoiding flicker during Radix tab switches.
 */
export const useDrawerNavigationStore = (): ((ids: string[]) => void) => {
  return store.setIds
}

/**
 * Called by drawer components to read navigation state and get nav callbacks.
 * Subscribes to the store and computes prev/next from the current URL param.
 */
export const useDrawerNavigation = (urlParam: UrlParam) => {
  const orderedIds = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot)
  const { urlParams, replaceParams } = useUrlParams()
  const currentId = urlParams[urlParam]

  const { previousId, nextId } = useMemo(() => {
    if (!currentId || orderedIds.length === 0) {
      return { previousId: undefined, nextId: undefined }
    }
    const index = orderedIds.indexOf(currentId)
    if (index === -1) {
      return { previousId: undefined, nextId: undefined }
    }
    return {
      previousId: index > 0 ? orderedIds[index - 1] : undefined,
      nextId: index < orderedIds.length - 1 ? orderedIds[index + 1] : undefined,
    }
  }, [orderedIds, currentId])

  const hasPrevious = previousId !== undefined
  const hasNext = nextId !== undefined

  const goToPrevious = useCallback(() => {
    if (previousId === undefined) return
    replaceParams({ update: [[urlParam, previousId]] })
  }, [previousId, replaceParams, urlParam])

  const goToNext = useCallback(() => {
    if (nextId === undefined) return
    replaceParams({ update: [[urlParam, nextId]] })
  }, [nextId, replaceParams, urlParam])

  return {
    hasPrevious,
    hasNext,
    goToPrevious,
    goToNext,
  }
}
